import { useState } from 'react';
import { css } from '@emotion/react';
import { useAppSettingsShallowStore } from '@frontend/app-settings-store';
import { GlobalBannerDynamic } from '@frontend/global-info-banner';
import { useTranslation } from '@frontend/i18n';
import { SoftphoneStateInterface } from '@frontend/softphone-state';
import { TAB_ID } from '@frontend/tab-management';
import { TextLink } from '@frontend/design-system';

export const SoftphoneInAnotherTabRibbon = ({
  isSoftphoneInAnotherBrowser,
}: {
  isSoftphoneInAnotherBrowser: boolean;
}) => {
  const { t } = useTranslation('softphone');
  const { activateCurrentTab } = SoftphoneStateInterface;

  const { hasActiveSoftphoneCalls, setOverrideSoftphoneCheck, activeTab } = useAppSettingsShallowStore(
    'hasActiveSoftphoneCalls',
    'setOverrideSoftphoneCheck',
    'activeTab'
  );
  const [showBanner, setShowBanner] = useState(true);
  const anotherTabHasActiveCalls = hasActiveSoftphoneCalls && activeTab !== TAB_ID;

  const actionButton = (
    <TextLink
      weight='bold'
      css={css`
        width: auto;
      `}
      onClick={() => {
        setOverrideSoftphoneCheck(true);
        activateCurrentTab();
      }}
    >
      {t('Use Softphone in This Tab')}
    </TextLink>
  );

  const message = anotherTabHasActiveCalls
    ? t(
        'It looks like you have Weave open on multiple tabs while on an active call. Please return to the tab with your active call and close all other tabs to prevent losing your active call or missing incoming calls.'
      )
    : !isSoftphoneInAnotherBrowser
    ? t(
        "It looks like you have Weave open on multiple tabs while you're connected to a Softphone. Your Softphone can only be used in one tab. You can return to the original tab or use the Softphone in this tab."
      )
    : t(
        'Your softphone has been detected in another application. Please close your other browser or application to use your softphone here. Refresh the page to try again.'
      );

  const title = !isSoftphoneInAnotherBrowser ? t('Multiple Tabs Detected') : t('Multiple Softphones Detected');

  if (!showBanner) return null;
  return (
    <GlobalBannerDynamic
      id='softphone-alerts-tabmanager'
      type='warning'
      title={title}
      message={message}
      isDismissible={showBanner}
      onDismiss={() => {
        setShowBanner(false);
      }}
      action={{
        element: actionButton,
      }}
    />
  );
};
